import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, linkedSignal, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideColumns2,
  lucideEllipsisVertical,
  lucideEye,
  lucideFileDown,
  lucideLayoutDashboard,
  lucideListFilter,
  lucideLockKeyhole,
  lucideLockKeyholeOpen,
  lucideRoute,
} from '@ng-icons/lucide';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { BadgeDirective } from '@shared/directives/badge.directive';
import { INSTITUTS_MÉTIER } from '@shared/models/instituts-métier';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { DateTime } from 'luxon';

import { ButtonClosePanelComponent } from '@shared/components/sidepanel/button-close-panel/button-close-panel.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import {
  RéférentielLivrable,
  ÉtatRéférentielLivrable,
  ÉtatRéférentielLivrableNom,
} from '../../administration/gestion-référentiels/models/référentiel-livrable.model';
import { REFERENTIEL_TYPE } from '../../administration/gestion-référentiels/models/référentiel-type.enum';
import { ConvertReferentielTypeToStringPipe } from '../../administration/gestion-référentiels/pipes/convert-referentiel-type-to-string-pipe';
import { Roles } from '../../auth/enum/roles.enum';
import { UserStore } from '../../auth/store/user.store';
import { CartographieStore } from '../../cartographie/store/cartographie.store';
import { MenuItem } from '../../référentiel-métier-parcours/components/context-menu/context-menu.component';
import { ParcoursStore } from '../../référentiel-métier-parcours/store/parcours.store';
import { PaginateurComponent } from '../../shared/components/paginateur/paginateur.component';
import { ChipFiltreComponent } from '../components/chip-filtre/chip-filtre.component';
import { EtatReferentielLivrableComponent } from '../components/etat-referentiel-livrable/etat-referentiel-livrable.component';
import { EtatVerrouillageComponent } from '../components/etat-verrouillage/etat-verrouillage.component';
import { FiltresSuiviLivrablesComponent } from '../components/filtres-suivi-livrables/filtres-suivi-livrables.component';
import { RéférentielLivrableStore } from '../store/référentiel-livrable.store';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';

type LivrablePrincipalType = (typeof LivrablePrincipal)[keyof typeof LivrablePrincipal];

function estLivrablePrincipal(value?: string): value is LivrablePrincipalType {
  return !!value ? Object.values<string>(LivrablePrincipal).includes(value) : false;
}

const KEY_FILTRES = 'filtres';

@Component({
  selector: 'app-visualisation-referentiel',
  imports: [
    BrnSelectImports,
    HlmSelectImports,
    HlmIconImports,
    HlmButtonImports,
    HlmTooltipImports,
    SpinnerComponent,
    ReactiveFormsModule,
    PaginateurComponent,
    HlmTableImports,
    DatePipe,
    HlmCardImports,
    EtatReferentielLivrableComponent,
    BadgeDirective,
    ChipFiltreComponent,
    EtatVerrouillageComponent,
    HlmDropdownMenuImports,
  ],
  providers: [
    provideIcons({
      lucideEllipsisVertical,
      lucideRoute,
      lucideColumns2,
      lucideLayoutDashboard,
      lucideListFilter,
      lucideArrowRight,
      lucideLockKeyholeOpen,
      lucideLockKeyhole,
      lucideEye,
      lucideFileDown,
    }),
    ConvertReferentielTypeToStringPipe,
  ],
  templateUrl: './visualisation-referentiel.component.html',
})
export class VisualisationReferentielComponent implements OnInit, OnDestroy {
  readonly referentielLivrableStore = inject(RéférentielLivrableStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #router = inject(Router);
  readonly #cartographieStore = inject(CartographieStore);
  readonly #convertReferentielTypeToStringPipe = inject(ConvertReferentielTypeToStringPipe);
  readonly #userStore = inject(UserStore);
  readonly #canEditService = inject(CanEditService);
  readonly #parcoursStore = inject(ParcoursStore);
  readonly #referentielStore = inject(ReferentielStore);

  readonly livrables = computed(() => this.referentielLivrableStore.getPaginated());

  readonly cartographies = computed(() =>
    this.#cartographieStore.actives().map((x) => ({ key: x.slug, value: x.nom })),
  );
  readonly referentielTypes = Object.keys(REFERENTIEL_TYPE).map((x) => ({
    key: REFERENTIEL_TYPE[x as keyof typeof REFERENTIEL_TYPE],
    value: this.#convertReferentielTypeToStringPipe.transform(REFERENTIEL_TYPE[x as keyof typeof REFERENTIEL_TYPE]),
  }));
  readonly institutsMetier = [...INSTITUTS_MÉTIER].map(([key, value]) => ({ key: key, value: key + ' - ' + value }));
  readonly typesLivrable = Object.keys(LivrablePrincipal).map((x) => ({
    key: LivrablePrincipal[x as keyof typeof LivrablePrincipal],
    value: LivrablePrincipal[x as keyof typeof LivrablePrincipal],
  }));
  readonly statuts = [
    { key: ÉtatRéférentielLivrable.NON_DEMARRE, value: ÉtatRéférentielLivrableNom.NON_DEMARRE },
    { key: ÉtatRéférentielLivrable.PRODUCTION_DEMARREE, value: ÉtatRéférentielLivrableNom.PRODUCTION_DEMARREE },
    { key: ÉtatRéférentielLivrable.VALIDE_METIER, value: ÉtatRéférentielLivrableNom.VALIDE_METIER },
    {
      key: ÉtatRéférentielLivrable.VALIDE_SERVICE_FORMATION,
      value: ÉtatRéférentielLivrableNom.VALIDE_SERVICE_FORMATION,
    },
    { key: ÉtatRéférentielLivrable.VALIDE_CDM, value: ÉtatRéférentielLivrableNom.VALIDE_CDM },
    { key: ÉtatRéférentielLivrable.NON_CONCERNE, value: ÉtatRéférentielLivrableNom.NON_CONCERNE },
  ];
  readonly echeances = [
    { key: 'ONE_WEEK', value: 'Dans une semaine' },
    { key: 'ONE_MONTH', value: 'Dans un mois' },
    { key: 'THREE_MONTHS', value: 'Dans trois mois' },
  ];

  readonly filtres = computed(() => this.referentielLivrableStore.filtres() ?? {});
  readonly nbFiltres = computed(() => Object.values(this.filtres() ?? {}).filter((x) => !!x && x.length).length);
  readonly filtresActifs = linkedSignal(() => sessionStorage.getItem(KEY_FILTRES));
  readonly roles = Roles;

  constructor() {
    this.referentielLivrableStore.charger();
    effect(() => {
      const filtresActifs = this.filtresActifs();

      if (filtresActifs) {
        this.referentielLivrableStore.setFiltres(JSON.parse(filtresActifs));
      } else {
        const instituts = this.#userStore.instituts();
        if (instituts.length) {
          sessionStorage.setItem(
            KEY_FILTRES,
            //! TODO a revoir absolument
            `{"institutsMetiers": [${instituts.map((x) => `"${x}"`)}], "services": ["${REFERENTIEL_TYPE.METIER}"]}`,
          );
          this.filtresActifs.set(sessionStorage.getItem(KEY_FILTRES));
        }
      }
    });
  }

  ngOnInit(): void {
    this.referentielLivrableStore.setPageSize(10);
  }

  ngOnDestroy(): void {
    this.#sidepanelService.close();
  }

  protected nodeContextMenuItems(id: string) {
    let result: MenuItem<RéférentielLivrable>[] = [];
    const livrable = this.referentielLivrableStore.entityMap()[id];

    if (this.#userStore.isAdmin()) {
      if (livrable.estVerrouille !== undefined ? livrable.estVerrouille : this.datePassee(livrable)) {
        result.push({
          label: 'Déverrouiller',
          icon: 'lucideLockKeyholeOpen',
          action: () => {
            this.toggleVerrouillage(livrable);
          },
        });
      } else {
        result.push({
          label: 'Verrouiller',
          icon: 'lucideLockKeyhole',
          action: () => this.toggleVerrouillage(livrable),
        });
      }
      if (livrable.idParcours) {
        result.push({
          label: 'Télécharger ce parcours',
          icon: 'lucideFileDown',
          action: () => this.#exportParcours(livrable),
        });
      }
    } else if (livrable.estVerrouille !== undefined ? livrable.estVerrouille : this.datePassee(livrable)) {
      result.push({
        label: 'Demander de déverrouiller',
        icon: 'lucideLockKeyholeOpen',
        action: () => {
          const mailText = 'mailto:mhugon@compagnons-du-devoir.com';
          window.open(mailText, '_blank');
        },
      });
    }

    if (
      this.peutModifier(livrable.referentiel.equipeResponsable, livrable, [this.roles.REFERENTIEL]) &&
      livrable.nomLivrable === 'Référentiel Métier' &&
      !livrable.nomParcours
    ) {
      result.push({
        label: 'Télécharger ce référentiel',
        icon: 'lucideFileDown',
        action: () => this.#exportRéférentiel(livrable),
      });
    }
    return result;
  }

  ouvrirFiltres() {
    this.#sidepanelService.setComponentToDisplay(
      'Filtres du suivi des livrables',
      FiltresSuiviLivrablesComponent,
      undefined,
      undefined,
      ButtonClosePanelComponent,
    );
    this.#sidepanelService.open();
  }

  onChangeEtat(idLivrableReferentiel: string, etat: ÉtatRéférentielLivrable) {
    this.referentielLivrableStore.modifier({ id: idLivrableReferentiel, data: { etat } });
  }

  redirect(livrable: RéférentielLivrable, additionalPath?: string, queryParams?: Record<string, string | number>) {
    const { cartographie, referentiel } = livrable;

    const path = ['cartographies', cartographie.slug, 'référentiels', referentiel.slug];
    if (additionalPath) path.push(additionalPath);
    if (livrable.idParcours) path.push(livrable.idParcours);

    this.#router.navigate(path, { queryParams });
  }

  estLivrablePrincipal(nomLivrable: LivrablePrincipal | string) {
    return estLivrablePrincipal(nomLivrable);
  }

  peutModifier = (equipeResponsable: string, livrable: RéférentielLivrable, roles: string[]) => {
    const nomLivrablePrincipal = livrable.nomLivrableParent ?? livrable.nomLivrable;
    let predicate = this.#userStore.roles().includes(equipeResponsable) && !livrable.estVerrouille;
    if (estLivrablePrincipal(nomLivrablePrincipal)) {
      predicate =
        predicate &&
        this.#canEditService.check(
          equipeResponsable,
          roles,
          nomLivrablePrincipal,
          livrable.cartographie,
          livrable.referentiel,
        );
    }
    return predicate || this.#userStore.isAdmin();
  };

  getTypeLivrable(livrable: RéférentielLivrable) {
    return livrable.nomLivrableParent ?? livrable.nomLivrable;
  }

  updateCartographies(values: any) {
    this.#displayLoader();
    this.referentielLivrableStore.setFiltres({
      ...this.referentielLivrableStore.filtres(),
      cartographies: [...(values as string[])],
    });
  }

  updateServices(values: string[] | string | undefined) {
    this.#displayLoader();
    this.referentielLivrableStore.setFiltres({
      ...this.referentielLivrableStore.filtres(),
      services: [...(values as string[])],
    });
  }

  updateInstitutsMetiers(values: string[] | string | undefined) {
    this.#displayLoader();
    this.referentielLivrableStore.setFiltres({
      ...this.referentielLivrableStore.filtres(),
      institutsMetiers: [...(values as string[])],
    });
  }

  updateLivrables(values: string[] | string | undefined) {
    this.#displayLoader();
    this.referentielLivrableStore.setFiltres({
      ...this.referentielLivrableStore.filtres(),
      livrables: [...(values as LivrablePrincipal[])],
    });
  }

  updateStatuts(values: string[] | string | undefined) {
    this.#displayLoader();
    this.referentielLivrableStore.setFiltres({
      ...this.referentielLivrableStore.filtres(),
      statuts: [...(values as ÉtatRéférentielLivrable[])],
    });
  }

  updateEcheance(values: string[] | string | undefined) {
    this.#displayLoader();
    this.referentielLivrableStore.setFiltres({
      ...this.referentielLivrableStore.filtres(),
      echeance: values as 'ONE_WEEK' | 'ONE_MONTH' | 'THREE_MONTHS' | undefined,
    });
  }

  toggleVerrouillage(livrable: RéférentielLivrable) {
    const estVerrouille = livrable.estVerrouille === undefined ? false : livrable.estVerrouille;
    this.referentielLivrableStore.modifier({
      id: livrable.id,
      data: {
        etat: livrable.etat,
        estVerrouille: !estVerrouille,
      },
    });
  }

  datePassee(livrable: RéférentielLivrable) {
    const now = DateTime.now().toFormat('yyyy-MM-dd');
    const livrableCarto = livrable.cartographie.livrables.find((x) => x.nom === livrable.nomLivrable);
    const dateEcheance = livrableCarto?.dateEcheance?.toString();
    return dateEcheance ? now > dateEcheance : false;
  }

  #exportParcours(livrable: RéférentielLivrable) {
    this.referentielLivrableStore.setLoading(true);
    this.#parcoursStore.exportOneParcours({
      idParcours: livrable.idParcours!,
      idCartographie: livrable.cartographie.id,
      next: (blob: Blob) => this.downloadCSV(blob, livrable),
      error: () => this.referentielLivrableStore.setLoading(false),
    });
  }

  #exportRéférentiel(livrable: RéférentielLivrable) {
    this.referentielLivrableStore.setLoading(true);
    this.#referentielStore.exportReferentiel({
      idReferentiel: livrable.referentiel.id,
      next: (blob: Blob) => this.downloadXlsx(blob, livrable),
      error: () => this.referentielLivrableStore.setLoading(false),
    });
  }

  downloadCSV(blob: Blob, livrable: RéférentielLivrable) {
    const filename = `export-${livrable.nomParcours!.replace(' ', '-').toLocaleLowerCase()}-${livrable.referentiel.nom.replace(' ', '-').toLocaleLowerCase()}-${livrable.cartographie.nom.replace(' ', '-').toLocaleLowerCase()}.csv`;

    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv;charset=utf-8' }));
    downloadLink.href = url;
    downloadLink.download = filename;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(url);
    this.referentielLivrableStore.setLoading(false);
  }

  downloadXlsx(blob: Blob, livrable: RéférentielLivrable) {
    const filename = `export-${livrable.referentiel.nom.replace(' ', '-').toLocaleLowerCase()}-${livrable.cartographie.nom.replace(' ', '-').toLocaleLowerCase()}.xlsx`;

    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(
      new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    );
    downloadLink.href = url;
    downloadLink.download = filename;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(url);
    this.referentielLivrableStore.setLoading(false);
  }

  #displayLoader() {
    this.referentielLivrableStore.setLoading(true);
    setTimeout(() => this.referentielLivrableStore.setLoading(false), 500);
  }
}
